import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  ArrowLeft,
  User,
  Filter,
  RefreshCw,
  Clock,
  AlertCircle,
} from "lucide-react";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";
import { getCriteriaSelectionLines } from "../../features/search/utils/historyCriteria";
import { normalizeHistorySearchMode } from "../../features/search/utils/historySearchMetadata";

function AuditLogs() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEventType, setFilterEventType] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [dateRange, setDateRange] = useState({
    start: "",
    end: "",
  });
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedDetails, setExpandedDetails] = useState({});

  // Event type options
  const eventTypes = [
    { value: "", label: "All Events" },
    { value: "USER_LOGIN", label: "User Login" },
    { value: "USER_LOGOUT", label: "User Logout" },
    { value: "USER_CREATE", label: "User Created" },
    { value: "USER_UPDATE", label: "User Updated" },
    { value: "USER_DELETE", label: "User Deleted" },
    { value: "PASSWORD_CHANGE", label: "Password Changed" },
    { value: "PASSWORD_RESET", label: "Password Reset" },
    { value: "search", label: "Search Performed" },
    { value: "search_v2", label: "Search Performed (V2)" },
    { value: "PROMPT_UPDATE", label: "Prompt Updated" },
    { value: "COMPANY_CREATE", label: "Company Created" },
    { value: "COMPANY_UPDATE", label: "Company Updated" },
    { value: "COMPANY_DELETE", label: "Company Deleted" },
  ];

  // Load audit logs
  const loadLogs = useCallback(async (currentPage = 1) => {
    try {
      setError(null);
      setLoading(true);
      const axios = getAuthenticatedAxios();

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 20,
      });

      if (filterEventType) params.append("action", filterEventType); // Backend expects 'action' not 'event_type'
      if (filterUser) params.append("actor_id", filterUser); // Backend expects 'actor_id' not 'user_id'

      // Convert datetime-local to ISO format with timezone for backend
      if (dateRange.start) {
        const startDate = new Date(dateRange.start);
        params.append("start_date", startDate.toISOString());
      }
      if (dateRange.end) {
        const endDate = new Date(dateRange.end);
        params.append("end_date", endDate.toISOString());
      }

      const response = await axios.get(
        `${buildApiPath(API_ENDPOINTS.ADMIN.AUDIT_LOGS)}?${params}`,
      );

      // Handle paginated response
      // Backend returns { items: [], meta: {} } structure
      const logsData = response.data.items || [];
      const pagination = response.data.meta || {};

      setLogs(Array.isArray(logsData) ? logsData : []);
      setPage(pagination.page || currentPage);
      setTotalPages(pagination.total_pages || 1);
      setTotalLogs(pagination.total || 0);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
      setError("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [dateRange.end, dateRange.start, filterEventType, filterUser]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Filter logs based on search
  const filteredLogs = logs.filter((log) => {
    if (searchTerm === "") return true;

    const eventType = log.action || log.event_type || log.EventType || "";
    const userId = log.actor_id || log.user_id || log.UserId || "";
    const userEmail = log.actor_email || log.user_email || log.UserEmail || "";
    const details = JSON.stringify(log.details || log.Details || {});
    const ipAddress = log.ip_address || log.IpAddress || "";

    return (
      eventType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ipAddress.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Handle filter submit
  const handleFilterSubmit = () => {
    loadLogs(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm("");
    setFilterEventType("");
    setFilterUser("");
    setDateRange({ start: "", end: "" });
    loadLogs(1);
  };

  // Format event type for display (defensive against undefined/non-string)
  const formatEventType = (type) => {
    if (!type || typeof type !== "string") return "Unknown";
    return type
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get event type color
  const getEventTypeColor = (type) => {
    const upperType = type?.toUpperCase();
    if (upperType?.includes("LOGIN")) return "bg-green-100 text-green-800";
    if (upperType?.includes("LOGOUT")) return "bg-gray-100 text-gray-800";
    if (upperType?.includes("CREATE")) return "bg-blue-100 text-blue-800";
    if (upperType?.includes("UPDATE")) return "bg-yellow-100 text-yellow-800";
    if (upperType?.includes("DELETE")) return "bg-red-100 text-red-800";
    if (upperType?.includes("PASSWORD")) return "bg-purple-100 text-purple-800";
    if (upperType?.includes("SEARCH")) return "bg-indigo-100 text-indigo-800";
    return "bg-gray-100 text-gray-800";
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatSearchMode = (value) => {
    const normalized = normalizeHistorySearchMode(value);
    if (normalized === "basic") return "Basic";
    if (normalized === "pro") return "Pro";
    return "";
  };

  const formatCredits = (value) => {
    const count = Number(value);
    if (!Number.isFinite(count) || count < 1) return "";
    return `${count} ${count === 1 ? "credit" : "credits"}`;
  };

  // Format details with expandable view for complex data
  const toggleDetailExpansion = (logId) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const formatDetails = (details, logId, eventType) => {
    if (!details) return "";
    if (typeof details === "string") {
      try {
        details = JSON.parse(details);
      } catch {
        return details;
      }
    }

    // For search events, show a special detailed view
    if (
      ["search", "search_v2"].includes(
        String(eventType || "").toLowerCase(),
      )
    ) {
      const isExpanded = expandedDetails[logId];
      const criteriaLines = getCriteriaSelectionLines({
        criteriaSet: details.criteria_set,
      });
      const collapsedCriteriaLines = isExpanded
        ? criteriaLines
        : criteriaLines.slice(0, 2);
      const hasExpandableCriteria = criteriaLines.length > 2;
      const hasExpandableQuery =
        Boolean(details.query) && details.query.length > 50;
      const canExpand = hasExpandableQuery || hasExpandableCriteria;

      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {details.query && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                <strong>Query:</strong> "
                {details.query.length > 50 && !isExpanded
                  ? `${details.query.substring(0, 50)}...`
                  : details.query}
                "
              </span>
            )}
            {details.papers_found !== undefined && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs">
                <strong>Papers:</strong> {details.papers_found}
              </span>
            )}
            {details.preference && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-xs">
                <strong>Type:</strong> {details.preference.replace(/_/g, " ")}
              </span>
            )}
            {details.search_type && (
              <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-md text-xs">
                <strong>Search Type:</strong> {details.search_type}
              </span>
            )}
            {formatSearchMode(details.search_mode) && (
              <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-md text-xs">
                <strong>Mode:</strong> {formatSearchMode(details.search_mode)}
              </span>
            )}
            {formatCredits(details.charged_credits) && (
              <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-xs">
                <strong>Credits:</strong> {formatCredits(details.charged_credits)}
              </span>
            )}
            {details.query_intent && (
              <span className="px-2 py-1 bg-violet-100 text-violet-800 rounded-md text-xs">
                <strong>Intent:</strong> {details.query_intent}
              </span>
            )}
            {details.processing_time !== undefined && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs">
                <strong>Time:</strong>{" "}
                {typeof details.processing_time === "object"
                  ? parseFloat(details.processing_time.toString()).toFixed(2)
                  : parseFloat(details.processing_time).toFixed(2)}
                s
              </span>
            )}
            {details.search_id && (
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-md text-xs">
                <strong>ID:</strong> {details.search_id.substring(0, 8)}...
              </span>
            )}
            {criteriaLines.length > 0 && (
              <span className="px-2 py-1 bg-cyan-100 text-cyan-800 rounded-md text-xs">
                <strong>Criteria:</strong> {criteriaLines.length}
              </span>
            )}
          </div>
          {criteriaLines.length > 0 && (
            <div className="rounded-md bg-cyan-50 px-2 py-2 text-xs text-cyan-900">
              {collapsedCriteriaLines.map((line, idx) => (
                <div key={`${line}-${idx}`}>{line}</div>
              ))}
              {!isExpanded && hasExpandableCriteria && (
                <div className="mt-1 text-cyan-700">+{criteriaLines.length - 2} more</div>
              )}
            </div>
          )}
          {canExpand && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDetailExpansion(logId);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {isExpanded ? "Show less" : "Show details"}
            </button>
          )}
        </div>
      );
    }

    // For other events, use the original format
    const parts = [];

    // Handle other event details
    if (details.target_id) parts.push(`Target: ${details.target_id}`);
    if (details.target_email) parts.push(`Email: ${details.target_email}`);
    if (details.target_name) parts.push(`Name: ${details.target_name}`);
    if (details.changes)
      parts.push(`Changes: ${Object.keys(details.changes).join(", ")}`);
    if (details.reason) parts.push(`Reason: ${details.reason}`);
    if (details.prompt_key) parts.push(`Prompt: ${details.prompt_key}`);
    if (details.company_name) parts.push(`Company: ${details.company_name}`);

    return parts.join(" | ") || JSON.stringify(details);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/admin")}
                className="mr-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <FileText className="h-8 w-8 mr-3 text-blue-600" />
                  Audit Logs
                </h1>
                <p className="mt-1 text-gray-600">
                  System activity and security audit trail
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Event Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={filterEventType}
                onChange={(e) => setFilterEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                {eventTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange({ ...dateRange, start: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="datetime-local"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange({ ...dateRange, end: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleFilterSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredLogs.length} of {totalLogs} total logs
            </div>
            <div className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </div>
          </div>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingLogo size="md" message="Loading audit logs..." />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No audit logs found</p>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLogs.map((log, index) => {
                    // Handle field names from backend AuditLogResponse
                    const eventId = log.audit_id || log.event_id || log.EventId;
                    const eventType =
                      log.action || log.event_type || log.EventType;
                    const userId = log.actor_id || log.user_id || log.UserId;
                    const userEmail =
                      log.actor_email ||
                      log.user_email ||
                      log.UserEmail ||
                      "Unknown User";
                    const ipAddress = log.ip_address || log.IpAddress || "N/A";
                    const createdAt =
                      log.timestamp || log.created_at || log.CreatedAt;
                    const details = log.details || log.Details;
                    const uniqueKey = eventId
                      ? `${eventId}-${index}`
                      : `log-${index}`;

                    return (
                      <tr key={uniqueKey} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-gray-400" />
                            {formatTimestamp(createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getEventTypeColor(eventType)}`}
                          >
                            {formatEventType(eventType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-2 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {userEmail}
                              </div>
                              {userId && (
                                <div className="text-xs text-gray-500">
                                  ID: {userId.substring(0, 8)}...
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ipAddress}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="max-w-md">
                            {formatDetails(
                              details,
                              log.audit_id || log.id || index,
                              eventType,
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => loadLogs(page - 1)}
                disabled={page === 1}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => loadLogs(pageNum)}
                    className={`px-3 py-2 rounded-lg ${
                      page === pageNum
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && <span className="px-2">...</span>}

              <button
                onClick={() => loadLogs(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLogs;
